import React from 'react';
import { Box, Checkbox, FormControl, InputLabel, ListItemText, ListSubheader, MenuItem, Select, SelectProps } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { compose, head, keys, omitBy, size } from 'lodash/fp';
import { DefaultFormFields } from '../../../../ui-kit/FilterComponent/types';
import { MenuListProps } from '../../../../constants';
import { MoreFilters as MoreFiltersType } from './types';

interface MoreFiltersProps<T> {
    formData: T;
    setFormData: React.Dispatch<React.SetStateAction<T>>;
}

const getMoreFiltersText = (selectedOptions: string[], t: TFunction) => {
    const selectedCount = size(selectedOptions);
    if (selectedCount === 1) {
        return t(head(selectedOptions) || '');
    }
    if (selectedCount > 1) {
        return `${head(selectedOptions)} ${selectedCount - 1}`;
    }
    return t('More filters');
};

const MoreFilters = <T extends DefaultFormFields,>({ formData, setFormData }: MoreFiltersProps<T>) => {
    const { t } = useTranslation();
    const handleChange: SelectProps['onChange'] = event => {
        if (event.target.value) {
            const values = event.target.value as string[];
            if (values[0]) {
                const key = values[0] as keyof MoreFiltersType;
                setFormData({ ...formData, moreFilters: { ...formData.moreFilters, [key]: !formData.moreFilters[key] } });
            }
        }
    };

    const selectedOptions = compose(keys, omitBy(option => option === false))(formData.moreFilters);


    return (
        <Box minWidth={144} maxWidth={320} width='100%'>
            <FormControl fullWidth variant="outlined" size='small'>
                <InputLabel>{getMoreFiltersText(selectedOptions, t)}</InputLabel>
                <Select
                    variant='outlined'
                    label={t('More filters')}
                    renderValue={() => 'more filters'}
                    multiple name='moreFilters'
                    value={[]}
                    onChange={handleChange}
                    MenuProps={{
                        ...MenuListProps,
                        PaperProps: {
                            style: { maxHeight: 'unset' },
                        }
                    }}
                >
                    <ListSubheader>{t('Payment method')}</ListSubheader>
                    <MenuItem value='cash'>
                        <Checkbox color='primary' name='cash' checked={formData.moreFilters.cash} />
                        <ListItemText primary={t('Cash')} />
                    </MenuItem>
                    <MenuItem value='card'>
                        <Checkbox color='primary' name='card' checked={formData.moreFilters.card} />
                        <ListItemText primary={t('Credit Card')} />
                    </MenuItem>
                    <ListSubheader>{t('Fiscal registry report')}</ListSubheader>
                    <MenuItem value='reported'>
                        <Checkbox color='primary' name='reported' checked={formData.moreFilters.reported} />
                        <ListItemText primary={t('Reported')} />
                    </MenuItem>
                    <MenuItem value='notReported'>
                        <Checkbox color='primary' name='notReported' checked={formData.moreFilters.notReported} />
                        <ListItemText primary={t('Not reported')} />
                    </MenuItem>
                    <ListSubheader>{t('Discounted bills')}</ListSubheader>
                    <MenuItem value='discountedOnly'>
                        <Checkbox color='primary' name='discountedOnly' checked={formData.moreFilters.discountedOnly} />
                        <ListItemText primary={t('Show discounted bills only')} />
                    </MenuItem>
                    <ListSubheader>{t('Voided bills')}</ListSubheader>
                    <MenuItem value='voidedOnly'>
                        <Checkbox color='primary' name='voidedOnly' checked={formData.moreFilters.voidedOnly} />
                        <ListItemText primary={t('Show voided bills only')} />
                    </MenuItem>
                </Select>
            </FormControl>
        </Box>
    );
};

export default MoreFilters;
